import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { Grid } from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import useStyles from '../index.styles'

import AlertsTableEnterExitTime from './alert-table-column-renderers/alerts-table-enter-exit-time'
import AlertTableMultilineText from './alert-table-column-renderers/alerts-table-multiline-text'
import AlertsTableResponse from './alert-table-column-renderers/alerts-table-response'
import AlertTableSeverity, { alertTableSeverityComparator } from './alert-table-column-renderers/alerts-table-severity'
import AlertsTablePanelContent from './alerts-table-panel-content'
import { defaultAlertFilters } from './filter'

const columns: GridColDef<DeviceAlertTableRow>[] = [
  {
    field: 'severity',
    headerName: 'Severity',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertTableSeverity,
    sortComparator: alertTableSeverityComparator,
  },
  {
    field: 'name',
    headerName: 'Alert Name',
    editable: false,
    sortable: true,
    flex: 3,
    renderCell: AlertTableMultilineText,
  },
  {
    field: 'entryTime',
    headerName: 'Entry Time',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertsTableEnterExitTime,
  },
  {
    field: 'exitTime',
    headerName: 'Exit Time',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertsTableEnterExitTime,
  },
  {
    field: 'response',
    headerName: 'Response',
    editable: false,
    sortable: true,
    flex: 3,
    renderCell: AlertsTableResponse,
  },
] satisfies GridColDef<DeviceAlertTableRow>[]

const DeviceAlertsTable = ({ device, searchText, filters }: { device: IDeviceAPIObject; searchText: string; filters: DeviceAlertFilterForm }) => {
  const classes = useStyles

  const [searchParams] = useSearchParams()
  const alertId = searchParams.get('alertId')
  const { alerts } = device

  const rows: DeviceAlertTableRow[] = useMemo(() => {
    const allAlerts = alerts.map((alert) => {
      let severity: 'urgent' | 'warning' | 'info' | 'unknown' = 'unknown'
      if (alert.tags.includes('info')) severity = 'info'
      if (alert.tags.includes('warning')) severity = 'warning'
      if (alert.tags.includes('urgent')) severity = 'urgent'

      const sortedEvents = [...alert.events].sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
      const entryTimeEvent = sortedEvents.find((event) => event.event === 'enter')
      const exitTimeEvent = sortedEvents.find((event) => event.event === 'exit')

      const sortedResponses = [...alert.responses].sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
      const bestResponse =
        sortedResponses.find((response) => response.note === 'ack') ??
        sortedResponses.find((response) => response.note === 'decline') ??
        sortedResponses.find((response) => response.note === 'test')

      return {
        id: alert.alertId,
        severity,
        name: alert.ruleName,
        entryTime: entryTimeEvent?.updatedAt,
        exitTime: exitTimeEvent?.updatedAt,
        response: bestResponse,
        alert,
      } satisfies DeviceAlertTableRow
    })

    let filteredAlerts = allAlerts
    if (filters.severity !== defaultAlertFilters.severity) {
      filteredAlerts = filteredAlerts.filter((alert) => alert.severity === filters.severity)
    }
    if (filters.respondedOnly !== defaultAlertFilters.respondedOnly) {
      filteredAlerts = filteredAlerts.filter((alert) => alert.response)
    }

    if (searchText) {
      filteredAlerts = filteredAlerts.filter((alert) => {
        if (alert.name.toLowerCase().includes(searchText.toLowerCase())) return true
        if (alert.response?.user.firstName.toLowerCase().includes(searchText.toLowerCase())) return true
        if (alert.response?.user.lastName.toLowerCase().includes(searchText.toLowerCase())) return true
        return false
      })
    }

    if (alertId) {
      filteredAlerts = filteredAlerts.filter((alert) => alert.id === alertId)
    }

    return filteredAlerts
  }, [alertId, alerts, filters.respondedOnly, filters.severity, searchText])

  return (
    <Grid
      sx={{
        flexDirection: 'column',
      }}
    >
      <DataGridPro
        columns={columns}
        rows={rows}
        rowHeight={80}
        detailPanelExpandedRowIds={
          alertId ? new Set(rows.filter((row) => row.alert.responses.length > 0 && row.alert.alertId === alertId).map((row) => row.id)) : undefined
        }
        getDetailPanelContent={(params) => (params.row.alert.responses.length > 0 ? <AlertsTablePanelContent {...params} /> : undefined)}
        getDetailPanelHeight={() => 'auto'}
        autosizeOptions={{ expand: true }}
        getRowClassName={() => 'alertTableRow'}
        pagination={true}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        disableRowSelectionOnClick={true}
        sx={{
          '& .alertTableRow': classes.alertTableRow,
        }}
      />
    </Grid>
  )
}

export default DeviceAlertsTable
